import { useContext, useState } from "react";

import { Container } from "@mui/material";

import {
  Button,
  CopyButton,
  Modal,
  Pagination,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";

import { IconReload } from "@tabler/icons-react";

import dayjs from "dayjs";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";

import { Colors } from "../../lib/Data";
import { Endpoints } from "../../lib/Endpoints";
import { PaginatedResponse } from "../../lib/Responses";
import { ClientMessage } from "../../lib/Types";
import { usePerformRequest } from "../../lib/usePerformRequest";

import { getPaginationTotal } from "../../lib/Methods";
import { AppContext } from "../Dashboard";
import "./styles.scss";
import { useDisclosure } from "@mantine/hooks";
import AnimateOnLoad from "../../components/AnimateOnLoad";
import DatagridTable from "../../components/DatagridTable";

const Messages: React.FC = () => {
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });

  const {
    data: messages,
    isLoading: areMessagesLoading,
    reloadData: reloadMessages,
  } = usePerformRequest<PaginatedResponse<ClientMessage[]>>({
    method: "GET",
    route: Endpoints.GetClientMessages(pagination.page, pagination.limit),
  });
  const [currentMessage, setCurrentMessage] = useState<ClientMessage>();

  const [isMessageModalOpen, { open: OpenMessageModal, close: CloseMessageModal }] =
    useDisclosure(false);
  return (
    <AnimateOnLoad>
      <Container>
        {context && context.profile ? (
          <>
            <div className="sales-requests-container">
              <div className="flex-row align-center justify-between width-100">
                <Text className="component-header">Messages</Text>
                <div className="flex-row align-center">
                  <Button
                    color={Colors.BluePrimary}
                    style={{ borderRadius: "50px" }}
                    loading={areMessagesLoading}
                    onClick={reloadMessages}
                    leftSection={<IconReload size={16} />}
                  >
                    Reload
                  </Button>
                </div>
              </div>
              <br />
              <br />
              {areMessagesLoading ? (
                <MegaLoader />
              ) : (
                <>
                  {messages && messages.data && messages.data.length > 0 ? (
                    <>
                      <DatagridTable
                        isDataLoading={areMessagesLoading}
                        rows={messages.data.map((message) => {
                          return message;
                        })}
                        sx={{
                          "& .MuiDataGrid-row:hover": {
                            cursor: "pointer", // This will change the cursor to pointer on hover
                          },
                        }}
                        onRowClick={(params) => {
                          setCurrentMessage(params.row);
                          OpenMessageModal();
                        }}
                        columns={[
                          { flex: 1, field: "name", headerName: "Client Name" },
                          { flex: 1, field: "email", headerName: "Email" },
                          {
                            flex: 1,
                            field: "createdAt",
                            headerName: "Date",
                            width: 110,
                            renderCell: (params) => {
                              return (
                                <span>
                                  {dayjs(params.row.createdAt).format("ddd DD MMM YYYY")}
                                </span>
                              );
                            },
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <DisplayAlert
                      title="No messages Found"
                      children={<Text>No incoming messages from client side!</Text>}
                    />
                  )}
                </>
              )}
              <Modal
                centered
                opened={isMessageModalOpen}
                onClose={CloseMessageModal}
                withCloseButton={false}
              >
                <Text fw={600} c={Colors.GrayPrimary} fz={14}>
                  Name
                </Text>
                <Text mb={15} mt={-5} fw={500}>
                  {currentMessage?.name}
                </Text>
                <Text fw={600} c={Colors.GrayPrimary} fz={14}>
                  Email
                </Text>
                <CopyButton value={currentMessage?.email as string}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied!" : "Copy Email"}>
                      <Text
                        onClick={copy}
                        c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                        className="pointer"
                        mb={15}
                        mt={-5}
                        fw={600}
                      >
                        {currentMessage?.email}
                      </Text>
                    </Tooltip>
                  )}
                </CopyButton>
                <Text fw={600} c={Colors.GrayPrimary} fz={14}>
                  Subject
                </Text>
                <Text mb={15} mt={-5} fw={500}>
                  {currentMessage?.subject}
                </Text>
                <Text fw={600} c={Colors.GrayPrimary} fz={14}>
                  Message
                </Text>
                <Text mb={15} mt={-5} fw={500}>
                  {currentMessage?.message}
                </Text>
                <Button
                  color={Colors.BluePrimary}
                  radius={"50px"}
                  w={120}
                  onClick={CloseMessageModal}
                >
                  Close
                </Button>
              </Modal>
              <div className="flex-row align-center justify-center width-100">
                <Pagination
                  value={pagination.page}
                  onChange={(value) => {
                    setPagination({ ...pagination, page: value });
                  }}
                  my={40}
                  disabled={messages?.data.length === 0 || areMessagesLoading}
                  total={getPaginationTotal(messages?.totalRecords, pagination.limit)}
                />
              </div>
            </div>
          </>
        ) : (
          <MegaLoader />
        )}
      </Container>
    </AnimateOnLoad>
  );
};
export default Messages;
